import type {NavigateFunction} from 'react-router-dom'
import type {CustomFileData} from '@/shared/utils'
import type {StorageRoot} from '../core/storage'
import {openFileInViewer} from './external-viewer/open-in-viewer'
import {viewerTarget} from './fileBrowserRoot'

/**
 * Kind of a file-manager action, so the UI can treat viewer-opening actions uniformly (e.g. reuse
 * the optimistic loading placeholder, B1/B2) and distinguish them from actions that stay in the
 * browser without opening an object viewer.
 *
 *  - `OBJECT_OPENING`: resolves a file/object to a web viewer via the OBserver `/metadata` flow and
 *    opens it in a Results tab. These reuse {@link openFileInViewer} and therefore show a loading
 *    placeholder immediately, with a configurable message.
 *  - `ALL_IN_BROWSER`: produces/opens something client-side (e.g. an HTML report rendered in the
 *    html-viewer, a link, a download) without going through object-viewer resolution.
 *  - `FILE_OP`: plain storage operations (copy/cut/paste/rename/delete/share) — no viewer, no
 *    placeholder.
 */
export enum FileTableActionType {
  OBJECT_OPENING = 'object-opening',
  ALL_IN_BROWSER = 'all-in-browser',
  FILE_OP = 'file-op',
}

/** Context handed to an action's {@link FileTableAction.run}. */
export interface FileTableActionContext {
  selectedFiles: CustomFileData[]
  navigate: NavigateFunction
  /** Restore the centre column when opening a viewer after a Files+Chat focus collapse. */
  expandCenter?: () => void
  /** The root the selection is in; My Files when absent. */
  root?: StorageRoot
}

/**
 * Declarative descriptor for a file-manager action. Tagging actions with a {@link FileTableActionType}
 * lets viewer-opening actions share the optimistic-open UX (and their own loading message) instead of
 * each re-implementing it. Kept intentionally small — a typed descriptor consumed by the context
 * menu, not a plugin framework.
 */
export interface FileTableAction {
  id: string
  label: string
  type: FileTableActionType
  /** Placeholder message for OBJECT_OPENING actions; ignored by other types. Defaults per the loader. */
  loadingMessage?: string
  /** Whether the action is available for the current selection. */
  isVisible?: (ctx: FileTableActionContext) => boolean
  run: (ctx: FileTableActionContext) => void | Promise<void>
}

/** True when exactly one non-directory file is selected — the precondition for opening a viewer. */
export function isSingleFileSelected(ctx: FileTableActionContext): boolean {
  return ctx.selectedFiles.length === 1 && !ctx.selectedFiles[0].isDir
}

/**
 * The "Open" action: open the selected file in a web viewer (OBJECT_OPENING). Mirrors the
 * double-click behaviour and reuses {@link openFileInViewer}, so it gets the same instant loading
 * placeholder. This is the reference OBJECT_OPENING action; future viewer-opening actions follow the
 * same shape with their own label/loadingMessage.
 */
export const openInViewerAction: FileTableAction = {
  id: 'context-open',
  label: 'Open',
  type: FileTableActionType.OBJECT_OPENING,
  isVisible: isSingleFileSelected,
  run: ({selectedFiles, navigate, expandCenter, root}) => {
    const file = selectedFiles[0]
    if (!file || file.isDir) {
      return
    }
    return openFileInViewer({
      ...viewerTarget(root, file.id),
      displayName: file.name,
      tags: file.tags,
      navigate,
      expandCenter,
      loadingMessage: openInViewerAction.loadingMessage,
    })
  },
}
